import {useQuery} from '@apollo/client';
import {Spin, Table} from 'antd';
import {toast} from 'react-toastify';
import {useNavigate, useParams} from 'react-router-dom';
import {Button} from '@dabafinance/react-components';
import {NavTabsEnum} from 'utils/constants';
import {useSelector} from 'react-redux';
import {makeSelectTabOperations} from 'redux/store/auth';
import {GET_COLLECTION_BY_ID} from 'graphql/queries/collections';
import styles from '../../collectionsUI.module.scss';

/**
 * A collection's constituents under Instrument v2.
 *
 * This replaced a "Collection Companies" tab that listed the legacy issuer
 * companies and carried a duplicate of the stock reward bank create/edit
 * modals. That reward bank has its own screen at
 * `pages/rewardBanks/rewardBanksTabs/stockRewardBanks.js`, so the copy here was
 * dropped rather than moved.
 *
 * Unit rules come from `members` — the `instruments` array returns the
 * instruments without min/default/max.
 */
const CollectionConstituentsTab = () => {
  const {id} = useParams();
  const navigate = useNavigate();

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.Collections),
  );

  const {loading, data} = useQuery(GET_COLLECTION_BY_ID, {
    variables: {collectionId: id},
    errorPolicy: 'all',
    onError: error => toast.error(error?.message),
  });

  const collection = data?.getCollection?.data;
  const members = collection?.members ?? [];

  const columns = [
    {
      title: 'Instrument',
      key: 'instrument',
      render: (text, record) => (
        <div className={styles.nameCell}>
          {record?.instrument?.icon ? (
            <img
              src={record.instrument.icon}
              alt=""
              className={styles.nameIcon}
            />
          ) : (
            <div className={`${styles.nameIcon} ${styles.nameIconFallback}`}>
              {(record?.instrument?.ticker ?? '?').slice(0, 2)}
            </div>
          )}
          <div>
            <span className={styles.cellName}>
              {record?.instrument?.name ?? '—'}
            </span>
            <span className={styles.cellSub}>
              {record?.instrument?.ticker ?? '—'}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: 'Minimum',
      key: 'minimumUnits',
      width: 105,
      align: 'right',
      render: (text, record) => (
        <span className={styles.cellNum}>{record?.minimumUnits ?? '—'}</span>
      ),
    },
    {
      title: 'Default',
      key: 'defaultUnits',
      width: 105,
      align: 'right',
      render: (text, record) => (
        <span className={styles.cellNum}>{record?.defaultUnits ?? '—'}</span>
      ),
    },
    {
      title: 'Maximum',
      key: 'maximumUnits',
      width: 115,
      align: 'right',
      // Blank means no configured ceiling, which is different from zero.
      render: (text, record) =>
        record?.maximumUnits ? (
          <span className={styles.cellNum}>{record.maximumUnits}</span>
        ) : (
          <span className={styles.cellMuted}>No limit</span>
        ),
    },
    {
      title: 'What an investor buys',
      key: 'rule',
      render: (text, record) => (
        <span className={styles.cellMuted}>
          {record?.defaultUnits ?? '—'} unit
          {record?.defaultUnits === 1 ? '' : 's'} by default, between{' '}
          {record?.minimumUnits ?? '—'} and {record?.maximumUnits ?? 'no limit'}
        </span>
      ),
    },
  ];

  if (loading)
    return (
      <div className={styles.panel}>
        <div className={styles.loading}>
          <Spin size="large" />
          <h1>Loading constituents…</h1>
        </div>
      </div>
    );

  return (
    <div className={styles.panel}>
      <div className={styles.panelHead}>
        <h3>Constituents</h3>
        {members.length > 0 && (
          <span>
            Every instrument is listed on{' '}
            {collection?.exchange?.code ?? 'the collection exchange'}
          </span>
        )}
      </div>

      {members.length === 0 ? (
        <div className={styles.empty}>
          <h3>No constituents yet</h3>
          <p>
            A collection needs at least one public equity before investors can
            buy it. Add them from the edit screen.
          </p>
          <Button
            label="Edit collection"
            disabled={!tabOperations?.update}
            onClick={() => navigate(`/dashboard/collections/update/${id}`)}
          />
        </div>
      ) : (
        <div className={styles.table}>
          <Table
            dataSource={members}
            columns={columns}
            rowKey={record => record?.instrumentId}
            pagination={members.length > 10 ? {pageSize: 10} : false}
          />
        </div>
      )}
    </div>
  );
};

export {CollectionConstituentsTab};
export default CollectionConstituentsTab;
